import { ChevronLeft, Plus, Edit2, Trash2, Dumbbell, Home, Building2 } from 'lucide-react';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useApp } from '@/context/app-context';
import { uid } from '@/lib/helpers';
import type { GymProfile } from '@/lib/types';

export function GymProfiles() {
  const navigate = useNavigate();
  const { state, dispatch } = useApp();
  const [name, setName] = useState('');
  const [barWeight, setBarWeight] = useState('45');
  const [platesStr, setPlatesStr] = useState('45,35,25,10,5,2.5');

  const handleAddProfile = () => {
    if (!name) return;

    const newProfile: GymProfile = {
      id: uid(),
      name,
      barWeight: parseFloat(barWeight) || 45,
      availablePlates: platesStr
        .split(',')
        .map((p) => parseFloat(p.trim()))
        .filter((p) => !isNaN(p)),
    };

    const updated = [...state.gymProfiles, newProfile];
    dispatch({ type: 'SET_GYM_PROFILES', profiles: updated });

    setName('');
    setBarWeight('45');
    setPlatesStr('45,35,25,10,5,2.5');
  };

  const handleDeleteProfile = (id: string) => {
    const updated = state.gymProfiles.filter((p) => p.id !== id);
    dispatch({ type: 'SET_GYM_PROFILES', profiles: updated });

    if (state.activeGymId === id) {
      dispatch({ type: 'SET_ACTIVE_GYM', gymId: updated[0]?.id || '' });
    }
  };

  const handleSetActive = (id: string) => {
    dispatch({ type: 'SET_ACTIVE_GYM', gymId: id });
  };

  const getEquipmentIcon = (equipment: string) => {
    const icons: { [key: string]: string } = {
      'Barbell': '🏋️',
      'Dumbbells': '💪',
      'Bench': '🪑',
      'Rack': '⚙️',
      'Bands': '🔗',
      'Full Gym': '🏢',
      'Machines': '⚙️',
      'Cardio': '🏃',
      'Free Weights': '🏋️',
      'Pool': '🏊',
      'Bodyweight': '🧘',
    };
    return icons[equipment] || '•';
  };

  const getGymTypeIcon = (name: string) => {
    const lowerName = name.toLowerCase();
    if (lowerName.includes('home')) {
      return <Home size={20} style={{ color: '#FF6B35' }} />;
    } else if (lowerName.includes('commercial') || lowerName.includes('gym')) {
      return <Building2 size={20} style={{ color: '#FFA033' }} />;
    }
    return <Dumbbell size={20} style={{ color: '#A855F7' }} />;
  };

  return (
    <div
      className="min-h-screen flex flex-col"
      style={{ backgroundColor: '#18181B', width: '390px', margin: '0 auto' }}
    >
      {/* Header */}
      <div
        className="flex items-center justify-between p-4"
        style={{ borderBottom: '1px solid rgba(255,255,255,0.06)' }}
      >
        <button
          onClick={() => navigate(-1)}
          style={{
            minWidth: '44px',
            minHeight: '44px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <ChevronLeft size={24} style={{ color: '#F0F0F0' }} />
        </button>

        <div style={{ color: '#F0F0F0', fontSize: '16px', fontWeight: 700 }}>
          Gym Profiles
        </div>

        <div style={{ width: '44px' }} /> {/* Spacer */}
      </div>

      {/* Content */}
      <div className="flex-1 overflow-y-auto p-4">
        {/* Add Gym Form */}
        <div className="mb-6 p-4 rounded-xl" style={{ backgroundColor: '#27272A', border: '1px solid rgba(255,255,255,0.06)' }}>
          <p style={{ color: '#F0F0F0', fontSize: '14px', fontWeight: 700, marginBottom: '12px' }}>Add New Gym Profile</p>
          <div className="space-y-3 mb-4">
            <div>
              <label style={{ color: '#71717A', fontSize: '11px', fontWeight: 600, display: 'block', marginBottom: '6px' }}>
                GYM NAME
              </label>
              <input
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="e.g., Home Gym"
                style={{
                  backgroundColor: '#3F3F46',
                  color: '#F0F0F0',
                  fontSize: '14px',
                  padding: '10px 12px',
                  borderRadius: '8px',
                  width: '100%',
                  border: '1px solid rgba(255,255,255,0.06)',
                }}
              />
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <label style={{ color: '#71717A', fontSize: '11px', fontWeight: 600, display: 'block', marginBottom: '6px' }}>
                  BAR WEIGHT
                </label>
                <input
                  type="number"
                  value={barWeight}
                  onChange={(e) => setBarWeight(e.target.value)}
                  style={{
                    backgroundColor: '#3F3F46',
                    color: '#F0F0F0',
                    fontSize: '14px',
                    padding: '10px 12px',
                    borderRadius: '8px',
                    width: '100%',
                    border: '1px solid rgba(255,255,255,0.06)',
                  }}
                />
              </div>
              <div>
                <label style={{ color: '#71717A', fontSize: '11px', fontWeight: 600, display: 'block', marginBottom: '6px' }}>
                  PLATES (CSV)
                </label>
                <input
                  type="text"
                  value={platesStr}
                  onChange={(e) => setPlatesStr(e.target.value)}
                  style={{
                    backgroundColor: '#3F3F46',
                    color: '#F0F0F0',
                    fontSize: '12px',
                    padding: '10px 12px',
                    borderRadius: '8px',
                    width: '100%',
                    border: '1px solid rgba(255,255,255,0.06)',
                  }}
                />
              </div>
            </div>
          </div>
          <button
            onClick={handleAddProfile}
            className="w-full flex items-center justify-center gap-2 rounded-lg"
            style={{
              backgroundColor: '#FF6B35',
              color: '#18181B',
              padding: '12px',
              fontSize: '14px',
              fontWeight: 600,
            }}
          >
            <Plus size={18} />
            Add Gym
          </button>
        </div>

        {/* Gym Cards */}
        <div className="space-y-3">
          {state.gymProfiles.map((gym) => (
            <div
              key={gym.id}
              className="p-4 rounded-xl"
              style={{
                backgroundColor: '#27272A',
                border: state.activeGymId === gym.id ? '2px solid #FF6B35' : '1px solid rgba(255,255,255,0.06)',
              }}
            >
              {/* Gym Header */}
              <div className="flex items-start justify-between mb-3">
                <div className="flex items-center gap-3">
                  <div
                    className="rounded-lg flex items-center justify-center"
                    style={{
                      width: '48px',
                      height: '48px',
                      backgroundColor: '#3F3F46',
                    }}
                  >
                    {getGymTypeIcon(gym.name)}
                  </div>
                  <div>
                    <div
                      style={{
                        color: '#F0F0F0',
                        fontSize: '16px',
                        fontWeight: 700,
                        marginBottom: '2px',
                      }}
                    >
                      {gym.name}
                    </div>
                    <div style={{ color: '#71717A', fontSize: '12px' }}>
                      Bar: {gym.barWeight} | Plates: {(gym.availablePlates || []).length}
                    </div>
                  </div>
                </div>

                {/* Action Buttons */}
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => handleDeleteProfile(gym.id)}
                    className="rounded-lg flex items-center justify-center"
                    style={{
                      minWidth: '32px',
                      minHeight: '32px',
                      backgroundColor: '#3F3F46',
                      color: '#EF4444',
                    }}
                  >
                    <Trash2 size={14} />
                  </button>
                </div>
              </div>

              {/* Equipment/Plates Display */}
              {(gym.availablePlates || []).length > 0 && (
                <div className="mb-3 flex flex-wrap gap-2">
                  {gym.availablePlates?.map((plate, idx) => (
                    <div
                      key={idx}
                      className="px-2.5 py-1.5 rounded-lg flex items-center gap-1.5"
                      style={{
                        backgroundColor: '#3F3F46',
                        border: '1px solid rgba(255,255,255,0.06)',
                      }}
                    >
                      <span style={{ fontSize: '14px' }}>
                        {getEquipmentIcon(String(plate))}
                      </span>
                      <span
                        style={{
                          color: '#A1A1AA',
                          fontSize: '11px',
                          fontWeight: 600,
                        }}
                      >
                        {plate}
                      </span>
                    </div>
                  ))}
                </div>
              )}

              {/* Set Active Button */}
              <button
                onClick={() => handleSetActive(gym.id)}
                className="w-full rounded-lg"
                style={{
                  backgroundColor: state.activeGymId === gym.id ? '#FF6B35' : '#3F3F46',
                  color: state.activeGymId === gym.id ? '#18181B' : '#F0F0F0',
                  padding: '10px',
                  fontSize: '12px',
                  fontWeight: 600,
                }}
              >
                {state.activeGymId === gym.id ? '✓ Active' : 'Set Active'}
              </button>
            </div>
          ))}
        </div>

        {/* Info Box */}
        <div
          className="mt-4 p-4 rounded-lg"
          style={{
            backgroundColor: '#1F4428',
            border: '1px solid #4ADE80',
          }}
        >
          <div style={{ color: '#4ADE80', fontSize: '12px', fontWeight: 600, marginBottom: '4px' }}>
            💡 TIP
          </div>
          <div style={{ color: '#71717A', fontSize: '12px', lineHeight: '1.5' }}>
            Create different gym profiles to automatically adjust your workouts based on available equipment.
          </div>
        </div>
      </div>
    </div>
  );
}
